import { splitArrayEvenly } from '@Components/Utilities/ArrayHelpers';
import { useState, useEffect, Fragment, useMemo } from 'react';
import { bannerAdPositions, contactType, inquiryType, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { getBannerAd, gtmPushEvent, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, chunkArray } from '@Components/Utilities/SandhillsUtility';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import ListingControlBar from '@Components/Listings/ListingControlBar';
import Paging from '@Components/MuiWrappers/Paging';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import PartsListListing from '@Components/Parts/PartsListListing';
import PartsGridListing from '@Components/Parts/PartsGridListing';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue, useRecoilState } from 'recoil';
import AdditionalPageInformation from '@Components/Listings/AdditionalPageInformation';
import ListingOptions from '@Components/Shared/ListingOptions';
import debounce from 'lodash.debounce';

const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const DealerHeaderInfo = loadable(() => import('@Components/Shared/DealerHeaderInfo'));

const PartsListView = (props) => {

    const constants = { searchType: 'SearchType', page: 'page', format: 'Format', keywords: 'Keywords', manufacturer: 'Manufacturer', model: 'Model' };
    const topBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Top);
    const middleBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Middle);
    const bottomBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Bottom);
    const middleAdIndex = props.PartsListings.length >= props.DoubleClickAdShowAfterNumberOfListings ? props.DoubleClickAdShowAfterNumberOfListings - 1 : props.PartsListings.length - 1;
    const bottomAdIndex = props.DoubleClickAdShowSecondAdAfterNumberOfListings - 1;
    const [partNumber, setPartNumber] = useState(props.PartNumber);
    const [searchFields, setSearchFields] = useState(props.SearchFields);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [samModel, setSamModel] = useState(props.SAMModel);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [loadImagesForPrint, setLoadImagesForPrint] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState('');
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [returnFocusRef, setReturnFocusRef] = useState({});
    const [showShareButtons, setShowShareButtons] = useState(false);
    const [newView, setNewView] = useState(props.UseNewListPageDesign);
    const windowSize = useWindowSize();
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [listingPictureMap, setListingPictureMap] = useState({});
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const [expandAllDescriptions, setExpandAllDescriptions] = useState(props.ShowFullDescription);
    const [showAllSpecs, setShowAllSpecs] = useState(props.ShowAllSpecs);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        const chunkParts = () => {
            const width = window.innerWidth;
            let chunkSize;
            if (width > 1250) {
                chunkSize = 4;
            } else if (width > 1000) {
                chunkSize = 2;
            } else {
                chunkSize = 1;
            }

            const chunks = [];
            for (let i = 0; i < props.PartsListings.length; i += chunkSize) {
                chunks.push(props.PartsListings.slice(i, i + chunkSize));
            }

            const noImageRow = chunks.map((chunk) => {
                const hasImage = chunk.some((part) => part.MediaModel.Media.length > 0);

                return !hasImage;
            });

            const mappedListings = props.PartsListings.map((part, index) => {
                const chunkIndex = Math.floor(index / (window.innerWidth > 1250 ? 4 : window.innerWidth > 1000 ? 2 : 1));
                return noImageRow[chunkIndex];
            });

            setListingPictureMap(mappedListings);
        }

        cookieBannerResize();
        chunkParts();
        window.addEventListener('resize', cookieBannerResize);
        window.addEventListener('resize', chunkParts);

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
            window.removeEventListener('resize', chunkParts);
        };
    }, []);

    const toggleMobileFilter = (skipSearch) => {
        let newShowMobileFilters = !showMobileFilters;
        setShowMobileFilters(newShowMobileFilters);
        if (newShowMobileFilters) {
            setMobileSearch(window.innerWidth <= 785);
        } else {
            setMobileSearch(false);
            if (!skipSearch) {
                runFacetedSearch(false);
            }
        }
    };

    const clearAll = () => {
        window.scrollTo(0, 0);
        window.location = props.ClearAllUrl;
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false, []);

        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = props.SelectedFacets.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && (selectedField.ParentNameForSearch && selectedField.ParentNameForSearch != '')) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0) {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        if (fieldName.toLowerCase() == 'partnumber') {
            searchUrl = removeURLParameter(searchUrl, constants.format);
            searchUrl = removeURLParameter(searchUrl, constants.searchType);
        }

        if (fieldName.toLowerCase() == constants.manufacturer.toLocaleLowerCase()) {
            searchUrl = removeURLParameter(searchUrl, constants.model);
        }

        setSearchInProgress(true);
        window.location = searchUrl;
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName === pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];

            if (latestFieldValue.fieldName === field.FieldName && field.Type === 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchFields(fields);
                break;
            }
        }

        return pendingChanges;
    };

    const runFacetedSearch = (keepPage, latestFieldValue) => {
        setSearchInProgress(true);
        let pendingChanges = [];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        if (window.innerWidth <= 785) {
            setSearchInProgress(false);
            window.scrollTo(0, 0);
        }

        let searchUrl = buildUrl(keepPage, pendingChanges);
        if (pendingChanges.length > 0) {
            window.location = searchUrl;
        } else {
            setSearchInProgress(false);
        }
    };

    const buildUrl = (keepPage, pendingChanges) => {
        let searchUrl = props.SearchBaseUrl;
        pendingChanges.forEach((field) => {
            if (field.value) {
                searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                if (field.searchType) {
                    searchUrl = insertURLParameter(searchUrl, constants.searchType, field.searchType);
                }
            } else {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);
                if (field.searchType) {
                    searchUrl = removeURLParameter(searchUrl, constants.searchType);
                }
            }

            if (field.fieldName.toLowerCase() == 'partnumber') {
                searchUrl = removeURLParameter(searchUrl, constants.format);
            }
        });

        searchUrl = removeURLParameter(searchUrl, constants.page);
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, constants.page, props.PaginationModel.CurrentPage);
        }

        return searchUrl;
    };

    const performSearch = (type, pageNumber) => {
        setSearchInProgress(true);

        let searchUrl = props.SearchBaseUrl;
        if (partNumber) {
            searchUrl = insertURLParameter(searchUrl, 'PartNumber', partNumber);
        }

        if (type) {
            searchUrl = insertURLParameter(searchUrl, 'SearchType', type);
        }

        if (pageNumber && pageNumber > 1) {
            searchUrl = insertURLParameter(searchUrl, 'Page', pageNumber);
        }

        if (props.LocationID) {
            searchUrl = insertURLParameter(searchUrl, 'LocationID', props.LocationID);
        }

        if (props.DSCompanyID) {
            searchUrl = insertURLParameter(searchUrl, 'DSCompanyID', props.DSCompanyID);
        }

        if (props.OwnerCRMID) {
            searchUrl = insertURLParameter(searchUrl, 'OwnerCRMID', props.OwnerCRMID);
        }

        if (props.PartsSource) {
            searchUrl = insertURLParameter(searchUrl, 'PartsSource', props.PartsSource);
        }

        window.location = searchUrl;
    };

    const handleSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }

        performSearch(props.SearchType, 1);
    };

    const handlePageNumberChange = (page) => {
        performSearch(props.SearchType, page)
    };

    const onEmailModalToggle = () => {
        if (returnFocusRef) {
            setTimeout(() => {
                returnFocusRef.focus();
            }, 150);
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
    }

    const openEmailModal = (inquiry, contact, modalData) => {
        contact = contact || contactType.Retail;
        modalData = modalData || {};

        if (inquiry == inquiryType.EmailThis) {
            modalData.ModalTitle = translator.translate('sandhillssystem.share').toString();

            if (windowSize.isMobile && navigator.share) {
                navigator.share({
                    title: document.title,
                    url: window.location.href
                })
                    .then(() => gtmPushEvent(gtmEventType.MobileShare))
                    .catch(() => setShowShareButtons(true));
                return;
            } else {
                setShowShareButtons(true);
            }
        } else {
            setShowShareButtons(false);
        }

        setReturnFocusRef(modalData.Ref);
        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setSamModel({ ...samModel, ...modalData });
    };

    const onPrint = () => {
        gtmPushEvent(gtmEventType.printListDetail);
        setLoadImagesForPrint(true);
        window.print();
    };

    const renderBannerAd = (index) => {
        if (props.DoubleClickAdCollection?.AdUnits?.length > 0) {
            if (props.UseAlternateAdPlacement) {
                if (index === middleAdIndex && middleBannerAd) {
                    return (
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Middle}
                            ad={middleBannerAd}
                            contained
                        />
                    );
                } else if (index === bottomAdIndex && bottomBannerAd) {
                    return (
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Bottom}
                            ad={bottomBannerAd}
                            contained
                        />
                    );
                }
            } else if (index === middleAdIndex && bottomBannerAd) {
                return (
                    <DoubleClickAd
                        {...props.DoubleClickAdCollection}
                        position={bannerAdPositions.Bottom}
                        ad={bottomBannerAd}
                        contained
                    />
                );
            }
        }

        return (<></>);
    };

    return (
        <>
            <div id='parts-content' className={'parts-list-content' + (props.UseNewListPageDesign && !props.ShowLandingPage ? ' parts-redesign' : '')}>
                <section className='list-top-section'>
                    <div className='parts-title-and-breadcrumbs'>
                        {!props.IsHostedSite && <ComplexBreadcrumb BreadCrumbModels={props.BreadCrumbs} />}
                        {(!props.IsHostedSite && props.InventoryHeader) && !props.InventoryHeader?.EventID && !props.IsBothEventTypesDealerPage &&
                            <div className='dealer-tabs'>
                                <div className='dealer-tab-buttons cf'>
                                    {props.InventoryHeader.InventoryUrls && props.InventoryHeader.InventoryUrls.length > 0 && props.InventoryHeader.InventoryUrls.map(item =>
                                        <a key={item.DisplayText} className={'dealer-tab-button'} href={item.Url}>{item.DisplayText}</a>)}
                                </div>
                            </div>}
                        <div className='list-heading'>
                            <h1 className='parts-title'>
                                <span className='parts-title-text'><span dangerouslySetInnerHTML={{ __html: props.SearchTitle }}></span></span>
                            </h1>
                            {!props.ShowLandingPage && <span className='parts-listings-count'><span dangerouslySetInnerHTML={{ __html: props.ListingsCountTitle }}></span></span>}
                            <div className='media-buttons'>
                                {props.ShowPrintThis &&
                                    <button title={translator.translate('sandhills.print')} id='parts-list-page-print-this' type='button' className='print-this des-print-this ts-button' onClick={onPrint}>
                                        <i className='fas fa-print media-buttons__icon'></i>
                                    </button>}
                                {props.ShowEmailThis &&
                                    <button title={translator.translate('sandhillssystem.share')} id='parts-list-page-share-this' type='button' className='email-this des-email-this ts-button' onClick={() => openEmailModal(inquiryType.EmailThis)}>
                                        <i className='fas fa-share-alt media-buttons__icon'></i>
                                    </button>}
                            </div>
                        </div>
                    </div>
                    {topBannerAd && (!props.UseNewListPageDesign || props.ShowLandingPage) &&
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Top}
                            ad={topBannerAd}
                            contained
                        />}
                </section>
                {props.UseNewListPageDesign && !windowSize.isNotMobile && !props.ShowLandingPage && (
                    <div>
                        <ListingOptions
                            setShowAllSpecs={setShowAllSpecs}
                            setExpandAllDescriptions={setExpandAllDescriptions}
                            showAllSpecs={showAllSpecs}
                            showFullDescription={expandAllDescriptions}
                            isMobile
                            IsAuthenticated={currentUser.IsAuthenticated}
                        />
                        <div className='page-type-toggle'>
                            <div
                                role='button'
                                tabIndex='0'
                                onKeyDown={(e) => { if (e.key === 'Enter') setNewView(!newView) }}
                                id='list-toggle'
                                aria-label={translator.translate('sandhills.listview')}
                                title={translator.translate('sandhills.listview')}
                                className={newView ? 'sort' : 'sort active'}
                                onClick={() => { setNewView(!newView) }}
                            >
                                <i className='fas fa-list' />
                            </div>
                            <div
                                role='button'
                                tabIndex='0'
                                onKeyDown={(e) => { if (e.key === 'Enter') setNewView(!newView) }}
                                id='grid-toggle'
                                aria-label={translator.translate('sandhills.thumbnailview')}
                                title={translator.translate('sandhills.thumbnailview')}
                                className={newView ? 'sort active' : 'sort'}
                                onClick={() => { setNewView(!newView) }}
                            >
                                <i className='fas fa-table-cells-large' />
                            </div>
                        </div>
                    </div>
                )}
                <section className={'bottom-section' + (props.ShowLandingPage ? '' : ' parts')}>
                    <div className='side-container'>
                        {!props.ShowLandingPage &&
                            <FacetedSearch
                                runSearch={runFacetedSearch}
                                clearAll={clearAll}
                                clearFacet={clearFacetDebounced}
                                disabled={searchInProgress}
                                showMobileFilters={showMobileFilters}
                                toggleMobileFilter={toggleMobileFilter}
                                mobileSearch={mobileSearch}
                                IsDefaultSearch={props.IsDefaultSearch}
                                SearchFields={searchFields}
                                SelectedFacets={props.SelectedFacets}
                                SearchBaseURL={props.SearchBaseUrl}
                                ListingsCount={props.PartsListings ? props.PartsListings.length : 0}
                            />}
                    </div>
                    <div id='main-content' className={'parts-list-content' + (props.ShowLandingPage ? ' no-parts' : '')}>
                        {props.ShowLandingPage &&
                            <div className='parts-search-container'>
                                <div className='parts-search-content'>
                                    <div className='parts-search'>
                                        <div className='parts-search-title'>
                                            <label htmlFor='PartNumber'><strong><TranslationText tKey='sandhills.searchforparts' /></strong></label>
                                        </div>
                                        <form className='parts-search-form' onSubmit={handleSubmit}>
                                            <div className='part-search-partnumber-container'>
                                                <input id='PartNumber' type='text' name='PartNumber' className='part-search-partnumber' placeholder={translator.translate('sandhills.partnum')} defaultValue={props.PartNumber} onChange={(e) => setPartNumber(e.target.value)} />
                                                <button type='submit' className='ts-button parts-search-submit'>
                                                    <TranslationText tKey='sandhills.search' />
                                                </button>
                                            </div>
                                            <div className='parts-radio'>
                                                <fieldset className='ts__fieldset'>
                                                    <legend className='visually-hidden ts__legend'>{translator.translate('sandhills.selectasearchtype')}</legend>
                                                    <div className='search-type'>
                                                        <label>
                                                            <input type='radio' name='SearchType' value='Start' onChange={() => performSearch('Start', 1)} checked={props.SearchType !== 'Exact'} />
                                                            <TranslationText tKey='sandhills.startswith' />
                                                        </label>
                                                    </div>
                                                    <div className='search-type'>
                                                        <label>
                                                            <input type='radio' name='SearchType' value='Exact' onChange={() => performSearch('Exact', 1)} checked={props.SearchType === 'Exact'} />
                                                            <TranslationText tKey='Sandhills.TradePubWebSite.MVC.ExactMatch' />
                                                        </label>
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>}
                        {!props.ShowLandingPage &&
                            <>
                                {props.UseNewListPageDesign && windowSize.isNotMobile &&
                                    <div className='page-type-toggle-container'>
                                        <ListingOptions
                                            setShowAllSpecs={setShowAllSpecs}
                                            setExpandAllDescriptions={setExpandAllDescriptions}
                                            showAllSpecs={showAllSpecs}
                                            showFullDescription={expandAllDescriptions}
                                            IsAuthenticated={currentUser.IsAuthenticated}
                                        />
                                        <div className='page-type-toggle'>
                                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter') setNewView(!newView) }} id='list-toggle' aria-label={translator.translate('sandhills.listview')} title={translator.translate('sandhills.listview')} className={newView ? 'sort' : 'sort active'} onClick={() => { setNewView(!newView) }}>
                                                <i className='fas fa-list' />
                                            </div>
                                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter') setNewView(!newView) }} id='grid-toggle' aria-label={translator.translate('sandhills.thumbnailview')} title={translator.translate('sandhills.thumbnailview')} className={newView ? 'sort active' : 'sort'} onClick={() => { setNewView(!newView) }}>
                                                <i className='fas fa-table-cells-large' />
                                            </div>
                                        </div>
                                    </div>
                                }
                                {props.InventoryHeader &&
                                    <AdditionalPageInformation
                                        {...props.InventoryHeader}
                                        openEmailModal={openEmailModal}
                                        parts={true} />
                                }
                                {props.ErrorMessage && <div className='error-message'>{props.ErrorMessage}</div>}
                                <div className={'parts-listings' + (props.UseNewListPageDesign && newView ? ' grid' : '')}>
                                    {topBannerAd && props.UseNewListPageDesign &&
                                        <DoubleClickAd
                                            {...props.DoubleClickAdCollection}
                                            position={bannerAdPositions.Top}
                                            ad={topBannerAd}
                                            contained
                                        />
                                    }
                                    {(props.UseNewListPageDesign && newView) ?
                                        <>
                                            {props.PartsListings.map((item, index) =>

                                                <Fragment key={item.PartID}>
                                                    {item.GroupBannerText &&
                                                        <div className='part-group-banner'>{item.GroupBannerText}</div>}
                                                    <PartsGridListing
                                                        {...item}
                                                        partsSource={props.PartsSource}
                                                        openEmailModal={openEmailModal}
                                                        CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                                        ForceLoadImages={loadImagesForPrint}
                                                        NewView={newView}
                                                        NoImageRow={listingPictureMap[index]}
                                                        showAllSpecs={showAllSpecs}
                                                        expandDescription={expandAllDescriptions}
                                                    />
                                                    {renderBannerAd(index)}
                                                </Fragment>)}
                                        </>
                                        :
                                        <>
                                            {props.PartsListings.map((item, index) =>

                                                <Fragment key={item.PartID}>
                                                    {item.GroupBannerText &&
                                                        <div className='part-group-banner'>{item.GroupBannerText}</div>}
                                                    <PartsListListing
                                                        {...item}
                                                        partsSource={props.PartsSource}
                                                        openEmailModal={openEmailModal}
                                                        CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                                        ForceLoadImages={loadImagesForPrint}
                                                        NewView={newView}
                                                    />
                                                    {renderBannerAd(index)}
                                                </Fragment>)}
                                        </>
                                    }
                                </div>
                                {props.PaginationModel.TotalPages > 1 &&
                                    <Paging
                                        currentPage={props.PaginationModel.CurrentPage}
                                        totalPages={props.PaginationModel.TotalPages}
                                        onChange={handlePageNumberChange}
                                    />}
                            </>}
                    </div>
                    {props.ShowLandingPage && props.TopManufacturers.length > 0 &&
                        <div className='top-manufacturers drilldown-section'>
                            <div className='content'>
                                <strong className='part-label'><TranslationText tKey='sandhills.tradepubwebsite.mvc.topmanufacturers' /></strong>
                                <div className='section-container'>
                                    <div className='sub-section-container'>
                                        <div className='sub-section-content'>
                                            {splitArrayEvenly(props.TopManufacturers, 4).map(group =>
                                                <div className='links'>
                                                    {group.map(link =>
                                                        <div key={link.Url} className='links-container'>
                                                            <a href={link.Url} className='links-link'>{link.DisplayText}</a>
                                                        </div>)}
                                                </div>)}
                                        </div>
                                    </div>
                                </div>
                                {props.ViewAllManufacturers &&
                                    <div className="view-more-parts parts-all">
                                        <a href={props.ViewAllManufacturers.Url}>{props.ViewAllManufacturers.DisplayText}</a>
                                    </div>}
                            </div>
                        </div>}
                    {showEmailModal &&
                        <EmailModal
                            {...samModel}
                            toggleEmailModal={onEmailModalToggle}
                            selectedInquiry={selectedInquiry}
                            selectedContactType={selectedContactType}
                            DefaultCultureCode={props.DefaultCultureCode}
                            ShowSocialShareButtons={showShareButtons}
                        />}
                </section>
                {
                    (!currentSite.IsBetaSite && !props.UseNewListPageDesign) &&
                    <FacetedSearchMobileMenu
                        vip
                        sellUrl={props.SellYourEquipmentUrl}
                        filter={{
                            render: !props.ShowLandingPage,
                            toggle: toggleMobileFilter
                        }}
                        flyout={{
                            render: props.UseClassicViewFlyout,
                            url: props.ClassicFlyoutModel.ClassicViewURL,
                            img: props.ClassicFlyoutModel.ClassicViewImageURL
                        }}
                    />
                }
                {searchInProgress &&
                    <div className='searching-background' />}
                {(currentSite.IsMobileAppContext || (windowSize.isMobile && props.UseBottomMobilePadding)) &&
                    <div className='detail__mobile-padding-bottom' />
                }
            </div>
            {
                (currentSite.IsBetaSite || props.UseNewListPageDesign) && !props.ShowLandingPage &&
                <ListingControlBar
                    IsParts={true}
                    NoListings={props.PartsListings.length <= 0}
                    emailAlerts={currentSite.HasEmailAlerts}
                    IsBidCallerSite={currentSite?.Site?.IsBidcaller}
                    IsEF={props.IsEFEquipmentSearchPage}
                    IsAuctionSite={currentSite.IsAuctionSite}
                    IsHostedSite={props.IsHostedSite}
                    IsRY={currentSite?.Site?.IsRentalYard}
                    IsAV={currentSite?.Site?.IsAuctionValues}
                    IsOS={currentSite?.Site?.IsOtherStock}
                    sellUrl={props.SellYourEquipmentUrl}
                    filter={{
                        render: !props.ShowLandingPage,
                        toggle: toggleMobileFilter
                    }} />
            }
        </>
    );
};

export default PartsListView;
global.PartsListView = PartsListView;